import { Stack, Typography } from '@mui/material';
import {
  DataGridProps,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useDeepCompareMemo } from 'use-deep-compare';
import { FeatureEnum } from '../../../../services/data/impl/cube/cubeSlice';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { useBlueberryFeatureIcon } from '../../../commonUI/BlueberryIcons';
import GenericDataGrid from '../../../dataGridTables/GenericDataGrid';
import { useBlueberrySizeBucketLabels } from '../../../dataMesh/blueberry/hooks/useBlueberrySizeBucketLabels';
import { useBlueberrySizeTableData } from './hooks/useBlueberrySizeTableData';
import { BlueberrySizeDataModel } from './types';
type Props = {
  dataGridApiRef?: DataGridProps['apiRef'];
};
export const BlueberrySizeTable = ({ dataGridApiRef }: Props) => {
  const { t } = useTranslation();
  const { isLoading, error, dataset } = useBlueberrySizeTableData();
  const labelMap = useBlueberrySizeBucketLabels();

  const columns = useDeepCompareMemo<GridColDef<BlueberrySizeDataModel>[]>(
    () => [
      {
        field: 'size',
        headerName: t('Size (mm)'),
        renderCell: BlueberrySizeCellRenderer,
        sortable: false,
        type: 'string',
        valueFormatter: (value) => t(labelMap?.[value] ?? value),
      },
      {
        field: 'green',
        headerName: t('Green'),
        renderHeader: () => (
          <BlueberryColorHeader color={FeatureEnum.blueberry_Green} />
        ),
        type: 'number',
        valueFormatter: colorCountValueFormatter,
      },
      {
        field: 'yellow',
        headerName: t('Pink'),
        renderHeader: () => (
          <BlueberryColorHeader color={FeatureEnum.blueberry_Yellow} />
        ),
        type: 'number',
        valueFormatter: colorCountValueFormatter,
      },
      {
        field: 'purple',
        headerName: t('Purple'),
        renderHeader: () => (
          <BlueberryColorHeader color={FeatureEnum.blueberry_Purple} />
        ),
        type: 'number',
        valueFormatter: colorCountValueFormatter,
      },
      {
        field: 'blue',
        headerName: t('Blue'),
        renderHeader: () => (
          <BlueberryColorHeader color={FeatureEnum.blueberry_Blue} />
        ),
        type: 'number',
        valueFormatter: colorCountValueFormatter,
      },
    ],
    [labelMap, t]
  );

  const rows = useDeepCompareMemo<BlueberrySizeDataModel[]>(() => {
    if (!isLoading && !error) {
      return dataset ?? [];
    }
    return [];
  }, [dataset, isLoading, error]);

  return (
    <GenericDataGrid
      rows={rows}
      columns={columns}
      loading={isLoading}
      disableRowSelectionOnClick
      getRowId={(row) => row.size}
      apiRef={dataGridApiRef}
      forceHidePagination
    />
  );
};

/** Components */

const BlueberryColorHeader = ({
  color,
}: {
  color: Extract<
    FeatureEnum,
    | 'blueberry_Green'
    | 'blueberry_Yellow'
    | 'blueberry_Blue'
    | 'blueberry_Purple'
  >;
}) => {
  const getBlueberryIcon = useBlueberryFeatureIcon();

  let colorLabel = '';
  switch (color) {
    case FeatureEnum.blueberry_Blue:
      colorLabel = 'Blue';
      break;
    case FeatureEnum.blueberry_Green:
      colorLabel = 'Green';
      break;
    case FeatureEnum.blueberry_Yellow:
      colorLabel = 'Pink';
      break;
    case FeatureEnum.blueberry_Purple:
      colorLabel = 'Purple';
      break;
    default:
      break;
  }

  const { t } = useTranslation();
  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      {getBlueberryIcon(color, 0.8)}
      <Typography>{t(colorLabel)}</Typography>
    </Stack>
  );
};

const BlueberrySizeCellRenderer = ({
  value,
}: GridRenderCellParams<
  BlueberrySizeDataModel,
  BlueberrySizeDataModel['size']
>) => {
  const labelMap = useBlueberrySizeBucketLabels();
  const { t } = useTranslation();

  let displayValue: string | undefined = value;
  if (!value) {
    return 'UNKNOWN_SIZE_BUCKET';
  }

  if (value === 'unknown') {
    displayValue = t(labelMap?.[value] ?? value);
  } else {
    displayValue = labelMap?.[value];
  }

  return displayValue;
};

/** Utilities */
function colorCountValueFormatter(value: number, row: BlueberrySizeDataModel) {
  if (!value) {
    return '';
  }

  const valuePercent = (value / row.total) * 100;
  return `${value.toFixed(0)} (${valuePercent.toFixed(0)}%)`;
}
